// Utility funkce — naplnění postupně (time formatting, countdown, …)

export function formatIsoTimestamp(date: Date = new Date()): string {
  return date.toISOString();
}

// ---------------------------------------------------------------------------
// Kanonická identita zastávky — MUSÍ se shodovat s api/mobile_routes/_stopkey.py
// ---------------------------------------------------------------------------
// `branch_id` ("ID klienta") není globálně unikátní — stejné ID má víc klientů
// (ORLEN 201 i SUPERZOO 201). Identita = list (klient) + branch_id, formát
// `sheet::branch_id`. Tenhle klíč posílají frontendy backendu jako stop_id
// (události, overrides, storno, přesuny) a používají ho pro lokální matching.
export interface StopKeyParts {
  sheet?: string | null;
  branch_id?: string | null;
  row?: number | string | null;
  // Jen pro custom zastávky bez branch_id (legacy plány) — viz customStopId().
  name?: string | null;
  lat?: number | string | null;
  lng?: number | string | null;
}

export const CUSTOM_SHEET = "_custom";   // ručně přidaná zastávka z plánovače

/** FNV-1a 32bit → 8 hex znaků. MUSÍ dávat stejný výsledek jako `_fnv1a`
 *  v api/mobile_routes/_stopkey.py. */
function fnv1a(text: string): string {
  let h = 0x811c9dc5;
  const bytes = new TextEncoder().encode(text);
  for (const b of bytes) {
    h ^= b;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** Odvozené stabilní id pro custom zastávku bez branch_id (legacy plány).
 *  Plánovač do 2026-08 ukládal custom zastávky s prázdným branch_id a row = -1 →
 *  všechny sdílely klíč `_custom::row-1` a odklik jedné odklikl ostatní.
 *  Vrací "" bez názvu i souřadnic (volající pak spadne na `row`). */
export function customStopId(stop: StopKeyParts): string {
  const name = (stop.name ?? "").toString().trim().toLowerCase();
  // null / undefined / "" musí dát stejný výsledek jako float(None) v Pythonu → bez coords
  const num = (v: number | string | null | undefined): number =>
    v === null || v === undefined || v === "" ? NaN : Number(v);
  const lat = num(stop.lat);
  const lng = num(stop.lng);
  const coords =
    Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(5)},${lng.toFixed(5)}` : "";
  if (!name && !coords) return "";
  return "custom-" + fnv1a(`${name}|${coords}`);
}

export function stopKey(stop: StopKeyParts): string {
  const sheet = (stop.sheet ?? "").toString().trim();
  let bid = (stop.branch_id ?? "").toString().trim();
  if (!bid && sheet === CUSTOM_SHEET) bid = customStopId(stop);
  if (!bid) {
    const row = stop.row;
    bid = row !== null && row !== undefined && row !== "" ? `row${row}` : "";
  }
  // bez listu (custom/temp/collection — branch_id je už unikátní UUID/prefix): vrať holé id
  if (!sheet) return bid;
  return `${sheet}::${bid}`;
}

// ---------------------------------------------------------------------------
// Materiál / dotace — vlastní klíč (client::branch_id, lowercase)
// MUSÍ se shodovat s api/mobile_routes/_stopkey.py material_match_key.
// ---------------------------------------------------------------------------
// Materiál se zadává free-textem (branch_id + client_name) → zná jen klienta,
// ne sheet. Klíč proto `client::branch_id` lowercase (client_name ≈ sheet.lower(),
// sedí se stop.client). Pro collection/TRANK (branch_id 'collection-…') holé id.
export interface MaterialKeyParts {
  client?: string | null;      // stop.client NEBO material client_name
  branch_id?: string | null;
}

export function materialKey(parts: MaterialKeyParts): string {
  const bid = (parts.branch_id ?? "").toString().trim();
  const client = (parts.client ?? "").toString().trim().toLowerCase();
  if (client && !bid.startsWith("collection-")) return `${client}::${bid}`;
  return bid;
}

// ---------------------------------------------------------------------------
// Normalizace naskenované pečeti — MUSÍ se shodovat s api/mobile_routes/_seal.py
// ---------------------------------------------------------------------------
// Čtečky vracejí kolem holého kódu balast, který se pak nespáruje s pečetí
// z počítárny:
//   - AIM identifikátor symbologie `]` + písmeno + číslice (`]C0846611` = Code128,
//     `]C1` GS1-128, `]A0` Code39, `]F0` Codabar, `]I0` ITF) — přidává ML Kit,
//     není součástí dat pečeti,
//   - guard znaky Code39 (`*846611*`), uvozovací závorky, mezery/CR/LF z ručních
//     čteček v keyboard-wedge režimu.
// Ořežeme jen OKRAJE a jen ne-alfanumerické znaky — vnitřek (pomlčky v `D-000-54`)
// zůstává nedotčený.
export function normalizeSeal(raw: string | null | undefined): string {
  let s = (raw ?? "").toString().trim();
  s = s.replace(/^\][A-Za-z][0-9]/, "");            // AIM identifikátor symbologie
  s = s.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, ""); // guardy / závorky / bílé znaky
  return s;
}

// Rozloží composite stop_id "SHEET::503" na části pro ZOBRAZENÍ — uživatel nemá
// vidět composite, jen číslo (+ klienta zvlášť). Bez "::" je to holé ID.
export function splitStopKey(stopId: string | null | undefined): { client: string; branch_id: string } {
  const s = (stopId ?? "").toString();
  const i = s.indexOf("::");
  if (i < 0) return { client: "", branch_id: s };
  return { client: s.slice(0, i), branch_id: s.slice(i + 2) };
}
